import { packModules, type PackModule } from '@ly360/core';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

/** Modules the organisation's industry pack switches on (commerce, marketing, growth). */
export function useModule(module: PackModule): boolean {
  const { session } = useAuth();
  return packModules(session?.tenant.industryPack).includes(module);
}

export interface Store {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  kind: 'store' | 'online' | 'popup';
  city: string | null;
  area: string | null;
  phone: string | null;
  hours: string | null;
  sales30Minor: string;
  orders30: number;
}
export interface Product {
  id: string;
  sku: string;
  brand: string;
  name: string;
  nameAr: string | null;
  category: string;
  gender: 'women' | 'men' | 'unisex' | null;
  concentration: string | null;
  sizeMl: number | null;
  scentFamily: string | null;
  notes: string[];
  ownBrand: boolean;
  priceMinor: string;
  salePriceMinor: string | null;
  currency: string;
  stock: number | null;
  url: string | null;
  waiting: number;
  sold30?: number;
}
export interface OrderItem {
  productId: string | null;
  sku: string;
  name: string;
  quantity: number;
  unitPriceMinor: string;
}
export interface Order {
  id: string;
  number: string;
  personId: string | null;
  personName: string | null;
  storeId: string | null;
  storeName: string | null;
  storeNameAr: string | null;
  source: string;
  status: string;
  subtotalMinor: string;
  discountMinor: string;
  totalMinor: string;
  currency: string;
  couponCode: string | null;
  conversationId: string | null;
  campaignId: string | null;
  campaignName: string | null;
  attributionReason: 'offer_code' | 'ad_conversation' | 'campaign_message' | null;
  placedAt: string;
  staffName: string | null;
  items: OrderItem[] | null;
}
export interface CommerceProfile {
  orders: number;
  spentMinor: string;
  averageMinor: string | null;
  lastOrderAt: string | null;
  class: string;
  birthday: string | null;
  preferredStoreId: string | null;
  preferredStoreName: string | null;
  preferredStoreNameAr: string | null;
  currency: string;
  favouriteBrands: string[];
  favouriteFamilies: string[];
  replenishDue: {
    productId: string;
    brand: string;
    name: string;
    sizeMl: number | null;
    boughtAt: string;
    dueOn: string;
    stock: number | null;
    priceMinor: string;
  }[];
  waitlist: { productId: string; brand: string; name: string; stock: number | null; createdAt: string }[];
  recentOrders: Order[];
}
export interface CampaignResults {
  recipients: number;
  sent: number;
  skipped: number;
  skippedNoConsent: number;
  delivered: number;
  read: number;
  replied: number;
  failed: number;
  conversations: number;
  orders: number;
  revenueMinor: number;
  spendMinor: number;
  impressions: number;
  clicks: number;
  leads: number;
  postsPublished: number;
}
export interface Campaign {
  id: string;
  name: string;
  status: string;
  objective: string;
  channels: string[];
  segmentId: string | null;
  segmentName: string | null;
  segmentNameAr: string | null;
  occasion: string | null;
  messageEn: string | null;
  messageAr: string | null;
  templateName: string | null;
  offerCode: string | null;
  budgetMinor: string | null;
  currency: string;
  startsAt: string | null;
  endsAt: string | null;
  attributionDays: number;
  sentAt: string | null;
  createdAt: string;
  createdByName: string | null;
  approvedByName: string | null;
  ads: { platform: string; adIds: string[]; budgetMinor: string | null }[];
  posts: number;
  results?: CampaignResults;
}
export interface Reach {
  total: number;
  whatsapp: number;
  email: number;
  sms: number;
}
export interface Segment {
  id: string;
  name: string;
  nameAr: string | null;
  description: string | null;
  rules: { all: Rule[] };
  campaigns: number;
  reach: Reach;
}
export type Rule = { type: string } & Record<string, unknown>;

export const useStores = () =>
  useQuery({ queryKey: ['stores'], queryFn: () => api<{ items: Store[] }>('/v1/stores') });

export const useProducts = (enabled = true) =>
  useQuery({
    queryKey: ['products', 'all'],
    enabled,
    queryFn: () => api<{ items: Product[]; brands: string[] }>('/v1/products?limit=500'),
  });

export const useSegments = (enabled = true) =>
  useQuery({ queryKey: ['segments'], enabled, queryFn: () => api<{ items: Segment[] }>('/v1/segments') });

export const RETAIL_KEYS = [
  'orders',
  'products',
  'stores',
  'campaigns',
  'campaign',
  'segments',
  'growth',
  'commerce',
];

/** Price shown on the shelf: the sale price when there is one. */
export const shelfPrice = (p: Pick<Product, 'priceMinor' | 'salePriceMinor'>) =>
  Number(p.salePriceMinor ?? p.priceMinor);

/** Return on ad spend (revenue ÷ spend), or null without spend. */
export const roas = (revenueMinor: number, spendMinor: number) =>
  spendMinor > 0 ? revenueMinor / spendMinor : null;
